import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Backtest.css';
import './Backtest2.css';
import AiCommentary from './AiCommentary';
import LivePosition from './LivePosition';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULTS = { tech: 'QQQ', momentum: 'MTUM', defensive: 'GLD' };

const Backtest2 = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('3y');
  const [showAllTrades, setShowAllTrades] = useState(false);

  // Swappable symbol slots
  const [tech, setTech] = useState(DEFAULTS.tech);
  const [momentum, setMomentum] = useState(DEFAULTS.momentum);
  const [defensive, setDefensive] = useState(DEFAULTS.defensive);
  // Pending edit values (committed on Run)
  const [techInput, setTechInput] = useState(DEFAULTS.tech);
  const [momentumInput, setMomentumInput] = useState(DEFAULTS.momentum);
  const [defensiveInput, setDefensiveInput] = useState(DEFAULTS.defensive);

  const periodOptions = {
    '3y': '3 Years',
    '5y': '5 Years',
    '10y': '10 Years',
    'max': 'Max',
  };

  const runBacktest = async (force = false, opts = {}) => {
    const p = opts.period ?? period;
    const t = (opts.tech ?? tech).toUpperCase().trim();
    const m = (opts.momentum ?? momentum).toUpperCase().trim();
    const d = (opts.defensive ?? defensive).toUpperCase().trim();
    if (!t || !m || !d) {
      setError('All three symbols are required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Hysteresis hardcoded to 2.0% (mathematically verified structural threshold)
      const url = `${API_URL}/api/backtest2/run?force=${force}&period=${p}&tech=${t}&momentum=${m}&defensive=${d}&hysteresis_pct=0.02`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setResults(data);
      } else {
        setError(data.message || 'Backtest failed');
      }
    } catch (err) {
      setError('Connection error - please try again');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runBacktest(); }, []); // eslint-disable-line

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    runBacktest(false, { period: newPeriod });
  };

  const handleApplySymbols = () => {
    const t = techInput.toUpperCase().trim();
    const m = momentumInput.toUpperCase().trim();
    const d = defensiveInput.toUpperCase().trim();
    setTech(t); setMomentum(m); setDefensive(d);
    runBacktest(false, { tech: t, momentum: m, defensive: d });
  };

  const handleResetSymbols = () => {
    setTechInput(DEFAULTS.tech);
    setMomentumInput(DEFAULTS.momentum);
    setDefensiveInput(DEFAULTS.defensive);
    setTech(DEFAULTS.tech); setMomentum(DEFAULTS.momentum); setDefensive(DEFAULTS.defensive);
    runBacktest(false, { tech: DEFAULTS.tech, momentum: DEFAULTS.momentum, defensive: DEFAULTS.defensive });
  };

  const MetricCard = ({ label, value, subValue, color }) => (
    <div className="bt-metric-card">
      <span className="bt-metric-label">{label}</span>
      <span className="bt-metric-value" style={color ? { color } : {}}>{value}</span>
      {subValue && <span className="bt-metric-sub">{subValue}</span>}
    </div>
  );

  const formatCurrency = (val) => `$${val?.toLocaleString() || 0}`;
  const positionTagClass = (sym) => {
    if (!sym) return '';
    const s = sym.toLowerCase();
    if (s === tech.toLowerCase()) return 'bt2-tag-tech';
    if (s === momentum.toLowerCase()) return 'bt2-tag-momentum';
    if (s === defensive.toLowerCase()) return 'bt2-tag-defensive';
    return '';
  };

  return (
    <div className="bt-container" data-testid="backtest2-page">
      <div className="bt-header">
        <a href="/explore" className="bt-back-link">← Back to Dashboard</a>
        <h1 className="bt2-title">Strategy Backtest II</h1>
        <p className="bt-subtitle">150 / 50 / 20 Rotator Engine</p>
        <p className="bt-description">
          Daily 3-asset rotation. <strong>Macro regime check</strong> on the tech leg
          (close &gt; EMA150 AND EMA50 &gt; EMA150) gates equity exposure; otherwise route
          100% to the defensive asset. Inside risk-on, hold whichever risk asset
          ({tech} vs {momentum}) has the higher 20-day momentum score, with a
          locked <strong>2.0% hysteresis friction band</strong> to suppress whipsaw rotations.
          T+1 Open execution, 0.15% slippage per rotation.
        </p>
      </div>

      {/* Live Position widget — today's signal (consumed from backtest result, no extra API call) */}
      <LivePosition signal={results?.current_signal} key={`lp-${tech}-${momentum}-${defensive}`} />

      {/* Symbol slots — fully user-configurable */}
      <div className="bt2-symbol-config" data-testid="backtest2-symbol-config">
        <div className="bt2-symbol-grid">
          <div className="bt2-symbol-slot">
            <label className="bt2-slot-label">Tech / Macro <span className="bt2-slot-default">default {DEFAULTS.tech}</span></label>
            <input
              type="text"
              value={techInput}
              onChange={(e) => setTechInput(e.target.value.toUpperCase())}
              className="bt2-symbol-input bt2-input-tech"
              placeholder={DEFAULTS.tech}
              maxLength={6}
              data-testid="backtest2-input-tech"
            />
            <span className="bt2-slot-hint">EMA150/EMA50/SMA20 computed here</span>
          </div>
          <div className="bt2-symbol-slot">
            <label className="bt2-slot-label">Momentum <span className="bt2-slot-default">default {DEFAULTS.momentum}</span></label>
            <input
              type="text"
              value={momentumInput}
              onChange={(e) => setMomentumInput(e.target.value.toUpperCase())}
              className="bt2-symbol-input bt2-input-momentum"
              placeholder={DEFAULTS.momentum}
              maxLength={6}
              data-testid="backtest2-input-momentum"
            />
            <span className="bt2-slot-hint">SMA20 momentum scoring</span>
          </div>
          <div className="bt2-symbol-slot">
            <label className="bt2-slot-label">Defensive <span className="bt2-slot-default">default {DEFAULTS.defensive}</span></label>
            <input
              type="text"
              value={defensiveInput}
              onChange={(e) => setDefensiveInput(e.target.value.toUpperCase())}
              className="bt2-symbol-input bt2-input-defensive"
              placeholder={DEFAULTS.defensive}
              maxLength={6}
              data-testid="backtest2-input-defensive"
            />
            <span className="bt2-slot-hint">Risk-off destination</span>
          </div>
        </div>
        <div className="bt2-symbol-actions">
          <button onClick={handleApplySymbols} className="bt-run-btn" disabled={loading} data-testid="backtest2-apply-symbols">
            Apply &amp; Run
          </button>
          <button onClick={handleResetSymbols} className="bt2-reset-btn" disabled={loading} data-testid="backtest2-reset-symbols">
            Reset to Defaults
          </button>
        </div>
      </div>

      <div className="bt-actions">
        <div className="bt-period-selector" data-testid="backtest2-period-selector">
          <span className="bt-selector-label">Period:</span>
          {Object.entries(periodOptions).map(([key, label]) => (
            <button
              key={key}
              onClick={() => handlePeriodChange(key)}
              className={`bt-period-btn ${period === key ? 'active' : ''}`}
              disabled={loading}
              data-testid={`backtest2-period-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => runBacktest(true)} disabled={loading} className="bt-run-btn" data-testid="backtest2-run-btn">
          {loading ? 'Running...' : 'Re-run Backtest'}
        </button>
      </div>

      {results && !loading && (
        <div className="bt2-params" data-testid="backtest2-params">
          <span><strong>EMA150</strong> macro</span>
          <span><strong>EMA50</strong> regime</span>
          <span><strong>SMA20</strong> momentum</span>
          <span><strong>2.0%</strong> hysteresis</span>
          <span><strong>0.15%</strong> slippage</span>
          <span><strong>T+1 Open</strong> exec</span>
        </div>
      )}

      {loading && (
        <div className="bt-loading" data-testid="backtest2-loading">
          <div className="bt-spinner"></div>
          <p>Running {tech} / {momentum} / {defensive} 150-50-20 rotation...</p>
          <p className="bt-loading-sub">Initial run may take 30-90 seconds (Alpha Vantage fetch).</p>
        </div>
      )}

      {error && <div className="bt-error" data-testid="backtest2-error">{error}</div>}

      {results && !loading && (
        <>
          <div className="bt-period" data-testid="backtest2-period">
            Backtest Period: <strong>{results.backtest_period.start}</strong> to <strong>{results.backtest_period.end}</strong>
            {' '}({results.backtest_period.trading_days} trading days, {results.backtest_period.years} years)
            {' · '}<strong>{results.symbols.tech}</strong> / <strong>{results.symbols.momentum}</strong> / <strong>{results.symbols.defensive}</strong>
          </div>

          <div className="bt-metrics-section">
            <h2>Strategy Performance</h2>
            <div className="bt-metrics-row" data-testid="backtest2-strategy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.strategy_metrics.ending_value)} subValue={`from ${formatCurrency(results.strategy_metrics.starting_value)}`} />
              <MetricCard label="Total Return" value={`${results.strategy_metrics.total_return_pct > 0 ? '+' : ''}${results.strategy_metrics.total_return_pct}%`} color={results.strategy_metrics.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.strategy_metrics.cagr}%`} color={results.strategy_metrics.cagr >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="Max Drawdown" value={`-${results.strategy_metrics.max_drawdown}%`} color="#f59e0b" />
              <MetricCard label="Sharpe Ratio" value={results.strategy_metrics.sharpe_ratio} color={results.strategy_metrics.sharpe_ratio >= 1 ? '#22c55e' : '#f59e0b'} />
              <MetricCard label="Total Trades" value={results.strategy_metrics.total_trades} />
            </div>
          </div>

          <div className="bt-metrics-section benchmark">
            <h2>SPY Buy-and-Hold Benchmark</h2>
            <div className="bt-metrics-row" data-testid="backtest2-spy-metrics">
              <MetricCard label="Ending Value" value={formatCurrency(results.spy_benchmark.ending_value)} subValue="from $100,000" />
              <MetricCard label="Total Return" value={`${results.spy_benchmark.total_return_pct > 0 ? '+' : ''}${results.spy_benchmark.total_return_pct}%`} color={results.spy_benchmark.total_return_pct >= 0 ? '#22c55e' : '#ef4444'} />
              <MetricCard label="CAGR" value={`${results.spy_benchmark.cagr}%`} />
              <MetricCard label="Max Drawdown" value={`-${results.spy_benchmark.max_drawdown}%`} color="#f59e0b" />
            </div>
          </div>

          {(() => {
            const diff = results.strategy_metrics.total_return_pct - results.spy_benchmark.total_return_pct;
            const outperformed = diff > 0;
            const ddDiff = results.spy_benchmark.max_drawdown - results.strategy_metrics.max_drawdown;
            const ddBetter = ddDiff > 0;
            return (
              <div className={`bt-outperformance ${outperformed ? 'positive' : 'negative'}`} data-testid="backtest2-outperformance">
                Strategy {outperformed ? 'outperformed' : 'underperformed'} SPY by{' '}
                <strong>{Math.abs(diff).toFixed(1)} percentage points</strong>
                {' '}{ddBetter ? 'with' : 'but with'}{' '}
                <strong>{Math.abs(ddDiff).toFixed(1)}%</strong>
                {' '}{ddBetter ? 'less' : 'deeper'} drawdown
              </div>
            );
          })()}

          <AiCommentary
            which="2"
            period={period}
            extraQuery={`tech=${tech}&momentum=${momentum}&defensive=${defensive}&hysteresis_pct=0.02`}
            key={`bt2-${period}-${tech}-${momentum}-${defensive}`}
          />

          <div className="bt-chart-section" data-testid="backtest2-chart">
            <h2>Equity Curve: Strategy vs SPY Buy-and-Hold</h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={results.equity_curve} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 10 }} interval={Math.floor(results.equity_curve.length / 12)} />
                <YAxis stroke="#94a3b8" tickFormatter={v => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }}
                  formatter={(value, name) => [`${value}%`, name]}
                  labelFormatter={(label) => `Date: ${label}`}
                  itemSorter={(item) => (item.dataKey === 'portfolio_return' ? 0 : 1)}
                />
                <Legend />
                <Line type="monotone" dataKey="portfolio_return" name="Strategy" stroke="#06b6d4" strokeWidth={2.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="spy_return" name="SPY Buy & Hold" stroke="#64748b" strokeWidth={1.5} dot={false} strokeDasharray="5 5" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bt-trades-section" data-testid="backtest2-trades">
            <div className="bt-trades-header">
              <h2>Position Changes ({results.total_trades_list_count} total)</h2>
              {results.trades.length > 15 && (
                <button onClick={() => setShowAllTrades(!showAllTrades)} className="bt-toggle-btn" data-testid="backtest2-toggle-trades">
                  {showAllTrades ? 'Show Last 15' : 'Show All'}
                </button>
              )}
            </div>
            <div className="bt-table-wrapper">
              <table className="bt-trades-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Action</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Exec Price</th>
                    <th>Portfolio Value</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllTrades ? results.trades : results.trades.slice(-15)).map((trade, idx) => (
                    <tr key={idx}>
                      <td>{trade.trade_number}</td>
                      <td>{trade.date}</td>
                      <td>{trade.action}</td>
                      <td><span className={`bt-position-tag ${positionTagClass(trade.from)}`}>{trade.from}</span></td>
                      <td><span className={`bt-position-tag ${positionTagClass(trade.to)}`}>{trade.to}</span></td>
                      <td>${trade.price?.toFixed(2)}</td>
                      <td>${trade.portfolio_value?.toLocaleString()}</td>
                      <td className="bt2-reason">{trade.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Backtest2;
